/**
 * Admin → Bulk Notifications helpers (iter182): input rules, error wording and run summaries.
 *
 * Kept out of the page so they can be tested on their own. The input rules mirror the backend
 * (routers/admin/bulk_notifications.py:BulkNotifPayload), which stays the authority.
 */

export const TITLE_LIMIT = 80;
export const MESSAGE_LIMIT = 240;

const IN_FLIGHT = ["QUEUED", "SENDING"];
const UNSAFE_URL_CHARS = /[\s\u0000-\u001f\u007f]/;   // eslint-disable-line no-control-regex

function isHttpUrl(v) {
    try {
        const u = new URL(v);
        return (u.protocol === "http:" || u.protocol === "https:") && !!u.host;
    } catch {
        return false;
    }
}

/** ``null`` when acceptable (empty is fine — the field is optional), otherwise the message to show. */
export function validateImageUrl(value) {
    const v = (value || "").trim();
    if (!v) return null;
    if (v.length > 1000 || UNSAFE_URL_CHARS.test(v) || !isHttpUrl(v)) {
        return "Use a full image link starting with https:// (max 1000 characters).";
    }
    return null;
}

/** The app opens the link on tap: a path inside the app (``/coupons``) or a full web link. */
export function validateDeepLink(value) {
    const v = (value || "").trim();
    if (!v) return null;
    const inApp = v.startsWith("/") && !v.startsWith("//");
    if (v.length > 500 || UNSAFE_URL_CHARS.test(v) || !(inApp || isHttpUrl(v))) {
        return "Use an in-app path starting with / (e.g. /coupons) or a full https:// link.";
    }
    return null;
}

/** One per campaign; re-sent on every retry of it so the server never pushes the same campaign twice. */
export function newIdempotencyKey() {
    try {
        if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID().replace(/-/g, "");
    } catch { /* fall through */ }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function isInFlight(run) {
    return !!run && (run.in_progress === true || IN_FLIGHT.includes(run.status));
}

const FIELD_LABEL = {
    title: "Title", body: "Message", image_url: "Image URL", deep_link: "Deep link", target: "Audience",
    idempotency_key: "Request",
};

/**
 * Turn an API failure into ``{ message, maybeRunning }``. ``maybeRunning`` is true when the request may have
 * reached the server (no answer, or a gateway error), so the page reloads the history instead of implying nothing
 * happened. It used to show only "Send failed" whenever the answer carried no ``detail`` — including a timeout
 * while the server was still sending.
 */
export function describeApiError(err, action = "Send") {
    const res = err?.response;
    if (!res) {
        const timedOut = err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");
        return {
            maybeRunning: true,
            message: timedOut
                ? "The server didn't answer in time. The push may still be going out — check “Recent bulk sends” "
                  + "below. Tapping Send again is safe: the same campaign is never pushed twice."
                : "Couldn't reach the server. Check your internet connection and try again — the same campaign "
                  + "is never pushed twice.",
        };
    }
    const { status } = res;
    const detail = res.data?.detail;
    if (status === 401) return { maybeRunning: false, message: "Your session has expired. Sign in again, then retry." };
    if (status === 403) {
        return { maybeRunning: false, message: "Your account isn't allowed to send notifications for this store." };
    }
    if (Array.isArray(detail) && detail.length) {
        const parts = detail.map((d) => {
            const field = [...(d?.loc || [])].reverse().find((l) => FIELD_LABEL[l]);
            const msg = String(d?.msg || "is invalid").replace(/^Value error, /, "");
            return field ? `${FIELD_LABEL[field]}: ${msg}` : msg;
        });
        return { maybeRunning: false, message: parts.join(" · ") };
    }
    if (typeof detail === "string" && detail.trim()) {
        return { maybeRunning: status >= 500 && status !== 503, message: detail };
    }
    if (status >= 500) {
        return {
            maybeRunning: true,
            message: `Server error (HTTP ${status}). Check “Recent bulk sends” before trying again.`,
        };
    }
    return { maybeRunning: false, message: `${action} failed (HTTP ${status}).` };
}

const STATUS = {
    SENT: { tone: "success", label: "Sent" },
    PARTIAL: { tone: "warning", label: "Partly sent" },
    FAILED: { tone: "error", label: "Not sent" },
    INTERRUPTED: { tone: "warning", label: "Stopped" },
    SENDING: { tone: "info", label: "Sending…" },
    QUEUED: { tone: "info", label: "Queued" },
};

export function statusInfo(status) {
    return STATUS[status] || { tone: "info", label: status || "—" };
}

/**
 * The counters worth showing for one run. "Accepted" means Firebase or the browser push service took the message;
 * neither reports delivery to the phone, so nothing here says "delivered". Runs recorded before iter182 carry only
 * device counts and get ``legacy: true``.
 */
export function summarizeRun(run) {
    const s = run?.summary || {};
    const total = s.target_count ?? run?.target_count ?? 0;
    if (s.recipients_accepted === undefined) {
        return {
            legacy: true,
            total,
            rows: [
                { key: "total", label: "Recipients", value: total },
                { key: "sent", label: "Devices accepted", value: (s.sent_fcm || 0) + (s.sent_web || 0) },
                { key: "failed", label: "Devices failed", value: (s.failed_fcm || 0) + (s.failed_web || 0) },
                { key: "no_device", label: "No registered device", value: s.no_device || 0 },
            ],
        };
    }
    const rows = [
        { key: "total", label: "Recipients", value: total },
        { key: "accepted", label: "Accepted for delivery", value: s.recipients_accepted || 0 },
        { key: "failed", label: "Failed", value: (s.recipients_failed || 0) + (s.errors || 0) },
        { key: "no_device", label: "No registered device", value: s.no_device || 0 },
    ];
    if (s.deduped) rows.push({ key: "deduped", label: "Already received it", value: s.deduped });
    if (s.removed_tokens) rows.push({ key: "removed", label: "Expired tokens removed", value: s.removed_tokens });
    return { legacy: false, total, processed: s.processed ?? total, rows };
}
